/**
 * Shared Resident Table Component
 * Used in both operational (ResidentsPage) and audit (FacilityAuditPage) contexts
 * Mode prop controls read-only vs editable behavior
 */

import {
  Anchor,
  Table,
  Badge,
  Group,
  Text,
  Tooltip,
  Button,
  Avatar,
} from "@mantine/core";
import {
  IconUser,
  IconAlertTriangle,
  IconHeartbeat,
  IconAccessible,
} from "@tabler/icons-react";
import { useNavigate, Link } from "react-router-dom";
import { routePaths } from "../../lib/routePaths";
import {
  calculateAge,
  getCriticalAlerts,
  formatRoomInfo,
  formatResidentName,
  type ResidentSummary,
} from "../../utils/residentFormatters";
import commonClasses from "../../styles/common.module.css";
import { useResidentPhotoUrl } from "../../hooks/useResidentPhotoUrl";

// Narrowed from frontend's Database["public"]["Tables"]["residents"]["Row"]
// (a 5000+-line generated Supabase schema this package must not depend on)
// down to what this component and its formatter helpers actually read — see
// residentFormatters.ts's header note. Structurally compatible with the full
// row type, so every current caller needs no changes.
type Resident = ResidentSummary;

/**
 * US-101 RES-008: resolve the photo through the authenticated proxy hook
 * (`photo_url` is an S3 key, not a renderable URL). A per-row component is
 * required — `useResidentPhotoUrl` is a hook and can't be called inside .map().
 */
function ResidentAvatarCell({ resident }: { resident: Resident }) {
  const photoSrc = useResidentPhotoUrl(resident.id, resident.photo_url);
  return (
    <Avatar
      src={photoSrc}
      alt={formatResidentName(resident)}
      size="md"
      radius="xl"
    >
      <IconUser size={20} />
    </Avatar>
  );
}

export interface ResidentTableProps {
  residents: Resident[];
  mode: "operational" | "audit";
  onRowClick?: (residentId: string) => void;
  showActions?: boolean;
  loading?: boolean;
}

/**
 * ResidentTable - Displays residents in table format
 * @param residents - Array of residents to display
 * @param mode - 'operational' for full features, 'audit' for read-only
 * @param onRowClick - Optional custom click handler (overrides default navigation)
 * @param showActions - Show action buttons (default: true)
 */
export function ResidentTable({
  residents,
  mode,
  onRowClick,
  showActions = true,
}: ResidentTableProps) {
  const navigate = useNavigate();

  const handleRowClick = (residentId: string) => {
    if (onRowClick) {
      onRowClick(residentId);
    } else if (mode === "audit") {
      // Audit mode: no navigation (read-only view in current page)
      return;
    } else {
      // Operational mode: navigate to resident detail page
      navigate(routePaths.residentDetail({ id: residentId }));
    }
  };

  // Mirrors handleRowClick's own branching so the whole row is only made a
  // click target when something would actually happen — an explicit
  // onRowClick override always navigates; plain audit mode is read-only.
  const isRowClickable = !!onRowClick || mode !== "audit";

  return (
    <Table.ScrollContainer minWidth={980}>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Photo</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Age</Table.Th>
            <Table.Th>Room</Table.Th>
            <Table.Th>Critical Info</Table.Th>
            <Table.Th>Status</Table.Th>
            {showActions && <Table.Th>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {residents.map((resident) => {
            const alerts = getCriticalAlerts(resident);

            return (
              <Table.Tr
                key={resident.id}
                className={commonClasses.tableRow}
                onClick={
                  isRowClickable ? () => handleRowClick(resident.id) : undefined
                }
                style={{ cursor: isRowClickable ? "pointer" : undefined }}
              >
                <Table.Td>
                  <ResidentAvatarCell resident={resident} />
                </Table.Td>
                <Table.Td>
                  {/* A REAL link, not just a row onClick. The row carried
                      cursor:pointer with no role, tabindex or href — mouse and
                      touch worked, but keyboard users could not reach it, screen
                      readers announced a plain table row, and there was no
                      cmd-click to open a resident in a new tab. The row click
                      stays as a convenience; this is the accessible path.
                      Only in the default navigate case: an onRowClick override
                      or audit mode may not go to the detail page at all. */}
                  {isRowClickable && !onRowClick && mode !== "audit" ? (
                    <Anchor
                      component={Link}
                      to={routePaths.residentDetail({ id: resident.id })}
                      onClick={(e) => e.stopPropagation()}
                      fw={500}
                      size="sm"
                      c="inherit"
                      underline="hover"
                    >
                      {formatResidentName(resident)}
                    </Anchor>
                  ) : (
                    <Text fw={500} size="sm">
                      {formatResidentName(resident)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {calculateAge(resident.date_of_birth)} yrs
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {formatRoomInfo(resident)}
                  </Text>
                </Table.Td>
                {/* Critical medical alerts - Color-coded per guidelines */}
                <Table.Td>
                  {alerts.hasCriticalAlerts ? (
                    <Group gap={4}>
                      {alerts.hasDNR && (
                        <Tooltip label="DNR Status - Do Not Resuscitate">
                          <Badge color="purple" size="sm" variant="filled">
                            DNR
                          </Badge>
                        </Tooltip>
                      )}
                      {alerts.hasAllergies && (
                        <Tooltip label={`Allergies: ${alerts.allergiesText}`}>
                          <Badge
                            color="red"
                            size="sm"
                            leftSection={<IconAlertTriangle size={12} />}
                          >
                            Allergies
                          </Badge>
                        </Tooltip>
                      )}
                      {alerts.isBedbound && (
                        <Tooltip label="Bedbound - requires assistance">
                          <Badge
                            color="orange"
                            size="sm"
                            leftSection={<IconAccessible size={12} />}
                          >
                            Bedbound
                          </Badge>
                        </Tooltip>
                      )}
                      {alerts.usesWheelchair && (
                        <Tooltip label="Uses wheelchair">
                          <Badge color="blue" size="sm">
                            ♿
                          </Badge>
                        </Tooltip>
                      )}
                      {alerts.hasDiagnoses && (
                        <Tooltip label={`Diagnoses: ${alerts.diagnosesText}`}>
                          <Badge
                            color="gray"
                            size="sm"
                            leftSection={<IconHeartbeat size={12} />}
                          >
                            Dx
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                  ) : (
                    <Text size="xs" c="dimmed">
                      No alerts
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={resident.status === "active" ? "green" : "gray"}
                    size="sm"
                    variant="light"
                  >
                    {resident.status}
                  </Badge>
                </Table.Td>
                {showActions && (
                  <Table.Td>
                    <Button
                      variant="light"
                      size="xs"
                      aria-label={`View ${formatResidentName(resident)} profile`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(resident.id);
                      }}
                    >
                      View
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
